/**
 * Owner 2026-11-04 (migration 1630): every event needs a Lead / Event Rep so its days can be started and closed.
 * Behind the server switch system_config.event_lead_required (read through the event_lead_required RPC). The event
 * wizard and the edit form ask for one; "No Rep Required / Self-Service" events are exempt. The database refuses a
 * signed-in create without one and an edit that clears it (trigger trg_events_lead_required).
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../services/supabase";

export const LEAD_REQUIRED_MESSAGE =
  "Choose a Lead / Event Rep (or No Rep Required / Self-Service). Every event needs a lead rep to start and close its days.";

/** The error to show, or null. */
export function leadRequiredError(
  required: boolean,
  repId: string | null | undefined,
  isSelfService: boolean,
): string | null {
  if (!required || isSelfService || !!repId) {
    return null;
  }
  return LEAD_REQUIRED_MESSAGE;
}

/**
 * Owner 2026-11-04 (migration 1760): while the switch is off, saving without a lead is allowed but warned about --
 * start / close of the event day read the Sales Reps list's lead.
 */
export const LEAD_SOFT_WARNING =
  "No lead rep chosen. You can still save, but without a lead nobody can start or close this event's days, and managers won't be alerted if a day isn't closed. Choose a Lead / Event Rep (or No Rep Required / Self-Service).";

/** The soft warning to show (switch off, no rep, not self-service), or null. Never blocks a save. */
export function leadSoftWarning(
  required: boolean,
  repId: string | null | undefined,
  isSelfService: boolean,
): string | null {
  if (required || isSelfService || !!repId) {
    return null;
  }
  return LEAD_SOFT_WARNING;
}

/** The server switch; off on error (the database still enforces it when on). */
export function useEventLeadRequired(): boolean {
  const { data = false } = useQuery({
    queryKey: ["event-lead-required"],
    queryFn: async () => {
      const { data: on, error } = await supabase.rpc("event_lead_required");
      if (error) {
        return false;
      }
      return on === true;
    },
    staleTime: 60_000,
  });
  return data;
}
